import BookingModal from '../components/BookingModal';
import React, { useState, useEffect } from 'react';
import HeroSlider from '../components/HeroSlider';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import BookingTypeCards from '../components/BookingTypeCards';
import VIPSlider from '../components/VIPSlider';
import SponsorsGrid from '../components/SponsorsGrid';
import CTAButtons from '../components/CTAButtons';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Button, CircularProgress, Tooltip, useTheme, Grid, Chip, Paper, Card, CardContent, IconButton, useMediaQuery } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import PetsIcon from '@mui/icons-material/Pets';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';
import KingBedIcon from '@mui/icons-material/KingBed';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import GavelIcon from '@mui/icons-material/Gavel';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function HomePage() {
  // VIPs state for homepage
  const [vips, setVips] = useState([]);
  const [vipsLoading, setVipsLoading] = useState(true);
  const [vipsError, setVipsError] = useState(null);

  // Sponsors state for homepage
  const [sponsors, setSponsors] = useState([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(true);
  const [sponsorsError, setSponsorsError] = useState(null);

  useEffect(() => {
    setVipsLoading(true);
    api.get('/api/cms')
      .then(res => {
        const items = (res.data || []).filter(i => /^vip$/i.test(i.type) || i.type === 'VIP');
        const all = items.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
        setVips(all);
        setVipsError(null);
      })
      .catch(() => setVipsError('Failed to load VIPs'))
      .finally(() => setVipsLoading(false));
  }, []);

  useEffect(() => {
    setSponsorsLoading(true);
    setSponsorsError(null);
    api.get('/api/sponsors')
      .then(res => {
        setSponsors(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setSponsorsError('Failed to load sponsors'))
      .finally(() => setSponsorsLoading(false));
  }, []);

  // Highlights state for homepage
  const [highlights, setHighlights] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [highlightsError, setHighlightsError] = useState(null);
  const [activeHighlight, setActiveHighlight] = useState(0);
  const [dialogIndex, setDialogIndex] = useState(null);
  const [readingMode, setReadingMode] = useState(false);
  const [isHighlightsHover, setIsHighlightsHover] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0); // internal index for seamless loop
  const [transitionDisabled, setTransitionDisabled] = useState(false);

  // Remove immediate consecutive duplicates and keep a reasonable cap for the carousel
  const displayedHighlights = React.useMemo(() => {
    if (!Array.isArray(highlights)) return [];
    const out = [];
    for (let i = 0; i < highlights.length; i++) {
      const cur = highlights[i] || {};
      const prev = highlights[i - 1] || {};
      if ((cur.heading || '').trim() === (prev.heading || '').trim()) continue; // drop consecutive duplicate
      out.push(cur);
      if (out.length >= 12) break;
    }
    return out;
  }, [highlights]);

  // card width used for transform calculation
  const cardWidth = 360;

  // Autoplay: advance every 7s when not hovered, respect prefers-reduced-motion
  React.useEffect(() => {
    if (!displayedHighlights || displayedHighlights.length === 0) return;
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq && mq.matches) return; // don't autoplay if user prefers reduced motion
    if (isHighlightsHover) return;
    const t = setInterval(() => {
      setActiveIndex(ai => (ai + 1));
    }, 7000);
    return () => clearInterval(t);
  }, [displayedHighlights.length, isHighlightsHover]);

  // initialize activeIndex when displayedHighlights change
  React.useEffect(() => {
    if (!displayedHighlights || displayedHighlights.length === 0) return;
    setActiveIndex(displayedHighlights.length);
    setActiveHighlight(0);
  }, [displayedHighlights]);

  // keep logical activeHighlight in sync with activeIndex
  React.useEffect(() => {
    if (!displayedHighlights || displayedHighlights.length === 0) return;
    setActiveHighlight(((activeIndex % displayedHighlights.length) + displayedHighlights.length) % displayedHighlights.length);
  }, [activeIndex, displayedHighlights]);

  // handle seamless reset when we pass clones
  React.useEffect(() => {
    if (!displayedHighlights || displayedHighlights.length === 0) return;
    const L = displayedHighlights.length;
    if (activeIndex <= (L - 1) || activeIndex >= 2 * L) {
      const resetTo = activeIndex <= (L - 1) ? activeIndex + L : activeIndex - L;
      const timer = setTimeout(() => {
        setTransitionDisabled(true);
        setActiveIndex(resetTo);
        setTimeout(() => setTransitionDisabled(false), 40);
      }, 520);
      return () => clearTimeout(timer);
    }
  }, [activeIndex, displayedHighlights]);

  useEffect(() => {
    setHighlightsLoading(true);
    setHighlightsError(null);
    api.get('/api/cms')
      .then(res => {
        const items = (res.data || []).filter(i => i.type === 'highlight');
        const all = items.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
        setHighlights(all);
      })
      .catch(() => setHighlightsError('Failed to load highlights'))
      .finally(() => setHighlightsLoading(false));
  }, []);

  // No auto-rotate; show multiple highlights at once in a grid
  const theme = useTheme();
  // Format prices in USD for display
  const formatUSD = (val) => {
    try {
      if (val === null || typeof val === 'undefined' || val === '') return '';
      const s = String(val).trim();
      // Strip any non-digit, non-dot, non-comma, non-minus characters (removes "Tsh", "TZS", etc.)
      const cleaned = s.replace(/[^0-9.,-]/g, '');
      // Convert to Number by removing commas
      const num = Number(cleaned.replace(/,/g, ''));
      if (!isFinite(num) || Number.isNaN(num)) {
        // If it isn't a parseable number, fall back to prefixing the original digits/text
        const fallbackDigits = cleaned || s;
        return `$ ${fallbackDigits}`;
      }
      const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(num));
      return `$ ${formatted}`;
    } catch (e) {
      return `$ ${val}`;
    }
  };
  // Packages state for homepage
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);

  useEffect(() => {
    setPackagesLoading(true);
    api.get('/api/packages')
      .then(res => {
        // Accept both {packages:[]} and []
        if (Array.isArray(res.data)) setPackages(res.data);
        else if (res.data && Array.isArray(res.data.packages)) setPackages(res.data.packages);
        else setPackages([]);
      })
      .catch(() => setPackages([]))
      .finally(() => setPackagesLoading(false));
  }, []);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [sliderConfig, setSliderConfig] = useState({ width: 1200, height: 420, countdownDate: '2026-06-01T09:00' });
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [aboutContent, setAboutContent] = useState(null);
  const [aboutLoading, setAboutLoading] = useState(true);

  useEffect(() => {
    api.get('/api/cms/slider-config').then(res => {
      setSliderConfig(res.data);
    });
    // Fetch About Us content from CMS
    api.get('/api/cms?types=about')
      .then(res => {
        let aboutItem = null;
        if (Array.isArray(res.data)) {
          // Pick the most recently updated About item
          const aboutItems = res.data.filter(i => i.type === 'about');
          if (aboutItems.length > 0) {
            aboutItems.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
            aboutItem = aboutItems[0];
          }
        }
        setAboutContent(aboutItem?.contentJSON || null);
      })
      .catch(() => setAboutContent(null))
      .finally(() => setAboutLoading(false));
  }, []);

  useEffect(() => {
    if (!sliderConfig.countdownDate) return;
    const updateCountdown = () => {
      const now = new Date();
      const eventDate = new Date(sliderConfig.countdownDate);
      const diff = Math.max(0, eventDate - now);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setCountdown({ days, hours, minutes, seconds });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [sliderConfig.countdownDate]);

  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  return (
    <PublicLayout>
      <Box sx={{ pt: { xs: 2, sm: 3, md: 4 }, pb: { xs: 2, md: 4 }, minHeight: '100vh', background: theme => theme.palette.background.default, overflow: 'visible', width: '100%', boxSizing: 'border-box' }}>
        {/* 1. Hero Slider with dynamic size (taller on small screens) and overlay CTAs */}
        {(() => {
          const heroHeight = isXs ? Math.min(sliderConfig.height || 420, 280) : (sliderConfig.height || 420);
          return (
            <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
              <HeroSlider width={sliderConfig.width} height={heroHeight}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: { xs: 12, sm: 18, md: 32 }, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, px: { xs: 1.5, sm: 2, md: 6 }, zIndex: 1 }}>
                  <Box sx={{ width: '100%', maxWidth: 980, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'nowrap' }}>
                    {/* translucent pill panel behind CTAs to keep them visible against slider images */}
                    <Box sx={{
                      display: 'inline-flex',
                      gap: { xs: 1, sm: 1.5, md: 1.5 },
                      alignItems: 'center',
                      px: { xs: 1.5, sm: 2, md: 1.5 },
                      py: { xs: 0.8, sm: 1, md: 0.6 },
                      borderRadius: 999,
                      backdropFilter: 'blur(6px)',
                      WebkitBackdropFilter: 'blur(6px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
                      pointerEvents: 'auto',
                      background: theme => theme.palette.mode === 'dark' ? 'rgba(12,12,16,0.64)' : 'rgba(255,255,255,0.92)',
                      border: theme => `1px solid ${theme.palette.divider}`,
                      ml: { md: 2 },
                      flexWrap: { xs: 'nowrap', sm: 'nowrap' }
                    }}>
                      <Button
                        fullWidth={false}
                        size={isXs ? 'small' : 'medium'}
                        variant="contained"
                        color="primary"
                        onClick={() => setBookingOpen(true)}
                        sx={{ px: { xs: 1.2, sm: 2, md: 2 }, py: { xs: 0.5, sm: 0.65, md: 0.55 }, fontWeight: 700, borderRadius: 999, minWidth: { xs: 90, sm: 110, md: 140 }, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, boxShadow: '0 6px 18px rgba(0,0,0,0.28)' }}
                      >
                        Book Now
                      </Button>

                      <Button
                        fullWidth={false}
                        size={isXs ? 'small' : 'medium'}
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate('/packages')}
                        sx={{ px: { xs: 1.2, sm: 2, md: 2 }, py: { xs: 0.5, sm: 0.65, md: 0.55 }, fontWeight: 700, borderRadius: 999, minWidth: { xs: 95, sm: 120, md: 140 }, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}
                      >
                        View Packages
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </HeroSlider>
            </Box>
          );
        })()}
        {/* 2. About Us & Countdown */}
        <Box sx={{
          mt: { xs: 3, sm: 4, md: 6 },
          mb: { xs: 2, sm: 3, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          minHeight: { xs: 'auto', md: 160 },
          p: { xs: 1.5, sm: 2, md: 0 },
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          overflow: 'visible',
          boxSizing: 'border-box'
        }}>
          {/* Decorative gadgets (hidden on small screens) */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', top: -30, right: -30, width: 80, height: 80, bgcolor: 'primary.light', opacity: 0.13, borderRadius: '50%' }} />
          <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', bottom: -20, left: -20, width: 60, height: 60, bgcolor: 'secondary.light', opacity: 0.10, borderRadius: '50%' }} />
          {/* About Section (left) - image first, then text */}
          <Box sx={{ flex: 1.2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'center', px: { xs: 1, sm: 1.5, md: 4 }, py: { xs: 1.5, sm: 2, md: 4 }, zIndex: 1, borderRight: { md: '1.5px solid', xs: 'none' }, borderColor: { md: 'divider', xs: 'transparent' }, gap: { xs: 1.5, md: 0 }, overflow: 'hidden' }}>
            <Box sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', pr: { md: 3, xs: 0 }, minHeight: 0, width: { xs: '100%', md: 'auto' } }}>
              {aboutLoading ? null : aboutContent && aboutContent.image ? (
                <Box sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: 'none',
                  boxShadow: theme => theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
                  width: { xs: 120, sm: 140, md: 200 },
                  height: { xs: 120, sm: 140, md: 180 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme => theme.palette.mode === 'dark' ? '#181828' : '#fff',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  '&:hover': {
                    boxShadow: theme => theme.palette.mode === 'dark' ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(0,0,0,0.18)',
                    transform: 'translateY(-4px)',
                  }
                }}>
                  <img src={aboutContent.image} alt="About" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ) : (
                <Box sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: 'none',
                  boxShadow: theme => theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
                  width: { xs: 120, sm: 140, md: 200 },
                  height: { xs: 120, sm: 140, md: 180 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme => theme.palette.mode === 'dark' ? '#181828' : '#fff',
                  flexShrink: 0
                }}>
                  <img src="/assets/about.jpg" alt="About" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, pl: { md: 2, xs: 0 }, textAlign: { xs: 'center', md: 'left' }, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, height: '100%', justifyItems: 'center', gap: 1 }}>
              <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: { xs: 0.5, md: 1.5 }, letterSpacing: { xs: 0.5, md: 1 }, fontSize: { xs: '1.3rem', sm: '1.5rem', md: '2.4rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>
                {aboutContent?.heading || 'About Us'}
              </Typography>
              {aboutLoading ? (
                <CircularProgress sx={{ my: 2, alignSelf: 'center' }} />
              ) : aboutContent ? (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: '0.95rem', sm: '1rem', md: '1.18rem' }, fontWeight: 500, lineHeight: 1.6, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {aboutContent.text || 'Professional event management for conferences, expos, and VIP experiences. Join us for a world-class event!'}
                  </Typography>
                  {aboutContent.link && (
                    <Button
                      href={aboutContent.link}
                      target="_blank"
                      rel="noopener"
                      variant="contained"
                      color="primary"
                      size={isXs ? 'small' : 'medium'}
                      fullWidth={isXs}
                      sx={{ mb: { xs: 0, md: 2 }, fontWeight: 700, borderRadius: 2, px: { xs: 1.5, md: 3 }, py: { xs: 0.6, md: 1.2 }, maxWidth: { xs: '100%', md: 'auto' }, alignSelf: { xs: 'center', md: 'flex-start' }, fontSize: { xs: '0.85rem', md: '1rem' } }}
                    >
                      Learn More
                    </Button>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: '0.95rem', sm: '1rem', md: '1.18rem' }, fontWeight: 500, lineHeight: 1.6, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  Professional event management for conferences, expos, and VIP experiences. Join us for a world-class event!
                </Typography>
              )}
            </Box>
          </Box>
          {/* Countdown Section (right) */}
          <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1, px: { xs: 1.5, sm: 2, md: 4 }, py: { xs: 2, sm: 3, md: 4 }, overflow: 'hidden' }}>
            <Typography variant="h6" fontWeight="bold" color="secondary" sx={{ mb: { xs: 1.5, md: 2 }, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.3rem' }, lineHeight: 1.4 }}>Countdown to Event</Typography>
            <Box sx={{ display: 'flex', gap: { xs: 0.4, sm: 0.8, md: 1 }, alignItems: 'center', justifyContent: 'center', mb: 1, flexWrap: 'wrap', overflow: 'hidden' }}>
              <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: { xs: 0.8, sm: 1, md: 1.2 }, py: { xs: 0.5, sm: 0.6, md: 0.8 }, borderRadius: 1.5, fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 40, sm: 48, md: 54 }, textAlign: 'center', letterSpacing: { xs: 0.3, md: 1 } }}>
                {countdown.days}
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' }, color: 'primary.contrastText', mt: 0.3 }}>Days</Typography>
              </Box>
              <Box sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' }, fontWeight: 700, color: 'primary.main', mx: { xs: 0.25, md: 0.5 } }}>:</Box>
              <Box sx={{ bgcolor: 'secondary.main', color: 'primary.contrastText', px: { xs: 0.8, sm: 1, md: 1.2 }, py: { xs: 0.5, sm: 0.6, md: 0.8 }, borderRadius: 1.5, fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 40, sm: 48, md: 54 }, textAlign: 'center', letterSpacing: { xs: 0.3, md: 1 } }}>
                {countdown.hours}
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' }, color: 'primary.contrastText', mt: 0.3 }}>Hours</Typography>
              </Box>
              <Box sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' }, fontWeight: 700, color: 'primary.main', mx: { xs: 0.25, md: 0.5 } }}>:</Box>
              <Box sx={{ bgcolor: 'grey.800', color: 'primary.contrastText', px: { xs: 0.8, sm: 1, md: 1.2 }, py: { xs: 0.5, sm: 0.6, md: 0.8 }, borderRadius: 1.5, fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 40, sm: 48, md: 54 }, textAlign: 'center', letterSpacing: { xs: 0.3, md: 1 } }}>
                {countdown.minutes}
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' }, color: 'primary.contrastText', mt: 0.3 }}>Minutes</Typography>
              </Box>
              <Box sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' }, fontWeight: 700, color: 'primary.main', mx: { xs: 0.25, md: 0.5 } }}>:</Box>
              <Box sx={{ bgcolor: 'secondary.main', color: 'primary.contrastText', px: { xs: 0.8, sm: 1, md: 1.2 }, py: { xs: 0.5, sm: 0.6, md: 0.8 }, borderRadius: 1.5, fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 40, sm: 48, md: 54 }, textAlign: 'center', letterSpacing: { xs: 0.3, md: 1 } }}>
                {countdown.seconds}
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' }, color: 'primary.contrastText', mt: 0.3 }}>Seconds</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* 3. Why Tanzania Section */}
        <Box sx={{
          mt: { xs: 5, sm: 6, md: 8 },
          mb: { xs: 4, sm: 5, md: 6 },
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 3, sm: 4, md: 6 },
          maxWidth: 1200,
          mx: 'auto',
          width: '100%',
          overflow: 'visible',
          boxSizing: 'border-box'
        }}>
          <Box sx={{
            bgcolor: 'background.paper',
            borderRadius: { xs: 2, md: 4 },
            p: { xs: 2, sm: 3, md: 5 },
            boxShadow: 3,
            border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: { xs: 2, md: 3 }, letterSpacing: { xs: 0.5, md: 1 }, textAlign: { xs: 'center', md: 'left' }, fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2.2rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>
              Why Tanzania
            </Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: { xs: 2, md: 3 }, color: theme => theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main, textAlign: { xs: 'center', md: 'left' }, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.35rem' }, lineHeight: 1.5, overflow: 'hidden', wordBreak: 'break-word' }}>
              International Medical Tourism Expo
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2.5 }, overflow: 'hidden' }}>
              <Typography variant="body1" sx={{
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.1rem' },
                lineHeight: 1.7,
                textAlign: { xs: 'left', md: 'justify' },
                color: 'text.secondary',
                fontWeight: 500,
                overflow: 'hidden',
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}>
                Tanzania is positioning itself as a competitive destination for medical tourism through the Tanzania International Medical Tourism Expo. The initiative responds to the growing global and national burden of non‑communicable diseases (NCDs). Although approximately 80% of NCDs are preventable through early detection and proper care, many citizens remain unaware of the solutions already available within the country.
              </Typography>
              <Typography variant="body1" sx={{
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.1rem' },
                lineHeight: 1.7,
                textAlign: { xs: 'left', md: 'justify' },
                color: 'text.secondary',
                fontWeight: 500,
                overflow: 'hidden',
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}>
                This expo is designed to close that information gap by creating partnerships, raising awareness, and empowering the population with access to critical health screenings and education.
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Packages section removed by request */}
        {/* 4. VIP Guests & Speakers Section (dynamic, styled) */}
        <Box sx={{ mt: { xs: 5, sm: 6, md: 6 }, mb: { xs: 3, sm: 4, md: 4 }, position: 'relative', px: { xs: 1, sm: 1.5, md: 0 }, overflow: 'visible', boxSizing: 'border-box', width: '100%', maxWidth: 1400, mx: 'auto' }}>
          {/* Tourism style background with image - hide on xs to avoid heavy stripes */}
          <Box sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            opacity: theme => (theme.palette.mode === 'dark' ? 0.15 : 0.2),
            backgroundImage: 'url(https://i.postimg.cc/NMJd6L2M/pori.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          }} />
          {/* Gradient overlay on top of background image */}
          <Box sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(120deg, rgba(35,35,58,0.8) 60%, rgba(46,46,77,0.8) 100%)'
              : 'linear-gradient(120deg, rgba(243,247,250,0.85) 60%, rgba(224,242,241,0.85) 100%)',
          }} />
          {/* Abstract animal icons for tourism feel (hidden on small screens) */}
          <PetsIcon sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', left: 24, top: 24, fontSize: 120, color: theme.palette.mode === 'dark' ? '#fff2' : '#0001', zIndex: 2 }} />
          <EmojiNatureIcon sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', right: 24, bottom: 24, fontSize: 120, color: theme.palette.mode === 'dark' ? '#fff2' : '#0001', zIndex: 2 }} />
          <EmojiEventsIcon sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', right: 120, top: 40, fontSize: 90, color: theme.palette.mode === 'dark' ? '#fff1' : '#0001', zIndex: 2 }} />
          <Box sx={{ bgcolor: 'transparent', p: { xs: 1.5, sm: 2, md: 4 }, borderRadius: { xs: 2, md: 3 }, boxShadow: 0, textAlign: 'center', maxWidth: 1400, mx: 'auto', position: 'relative', zIndex: 3, minHeight: { xs: 'auto', sm: 600, md: 700 }, mb: { xs: 2, md: 3 }, overflow: 'visible', width: '100%', boxSizing: 'border-box' }}>
            <Typography variant="h4" fontWeight={900} color="primary" sx={{ mb: { xs: 1.5, sm: 2, md: 3 }, letterSpacing: { xs: 0.5, md: 1 }, fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2.2rem' }, px: { xs: 1, md: 0 }, overflow: 'hidden', wordBreak: 'break-word' }}>VIPs & Speakers</Typography>
            {vipsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 300, md: 500 } }}><CircularProgress /></Box>
            ) : vipsError ? (
              <Typography color="error">{vipsError}</Typography>
            ) : vips.length === 0 ? (
              <Typography color="text.secondary">No VIPs available at this time.</Typography>
            ) : (
              <>
                {/* Featured VIPs */}
                {vips.filter(v => v.subtype === 'main').length > 0 && (
                  <>
                    <Typography variant="h5" sx={{ mb: { xs: 1.5, md: 2 }, mt: 1, fontWeight: 800, color: 'secondary.main', letterSpacing: { xs: 0.3, md: 0.5 }, fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.5rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>Featured VIPs</Typography>
                    <Grid container spacing={{ xs: 1.5, md: 4 }} sx={{ mb: 4, justifyContent: 'center', alignItems: 'stretch' }}>
                      {vips.filter(v => v.subtype === 'main').slice(0, 2).map((m, i, arr) => (
                        <Grid item xs={12} md={arr.length === 1 ? 8 : 6} key={i} sx={{ display: 'flex', justifyContent: 'center', minHeight: { xs: 'auto', md: 400 } }}>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: i % 2 === 0 ? 'row' : 'row-reverse' },
                            alignItems: 'stretch',
                            borderRadius: { xs: 2, md: 4 },
                            overflow: 'hidden',
                            boxShadow: 10,
                            minHeight: { xs: 'auto', md: 380 },
                            bgcolor: theme.palette.mode === 'dark' ? '#181828' : '#fff',
                            position: 'relative',
                            width: '100%',
                            maxWidth: 700,
                            transition: 'transform 220ms, box-shadow 220ms',
                            '&:hover': { transform: 'translateY(-6px)', boxShadow: 16 },
                          }}>
                            {/* Image - Clean without quote overlay */}
                            <Box sx={{ width: { xs: '100%', md: 340 }, height: { xs: 200, md: '100%' }, overflow: 'hidden', position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
                              {m.image ? (
                                <img src={m.image} alt={m.title || m.name} style={{ width: '100%', height: '100%', objectFit: isXs ? 'contain' : 'cover', objectPosition: 'center' }} />
                              ) : <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.300' }} />}
                              <Chip label="FEATURED" color="secondary" size="small" sx={{ position: 'absolute', top: 12, left: 12, fontWeight: 800 }} />
                            </Box>
                            {/* Details with Quote */}
                            <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: { xs: 'center', md: 'flex-start' }, minWidth: 0, maxWidth: { xs: '100%', md: 320 }, textAlign: { xs: 'center', md: 'left' }, zIndex: 3, gap: 1, overflow: 'hidden' }}>
                              <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', mb: 0, textAlign: { xs: 'center', md: 'left' }, textShadow: theme.palette.mode === 'dark' ? '0 2px 8px #0008' : '0 2px 8px #fff8', letterSpacing: { xs: 0.3, md: 0.5 }, fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' }, whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'visible', lineHeight: 1.3 }}>{m.title || m.name}</Typography>
                              {m.description && <Typography variant="body1" sx={{ mt: 0.5, maxWidth: '100%', color: theme.palette.mode === 'dark' ? '#fff' : '#333', fontWeight: 500, fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>{m.description}</Typography>}
                              {m.quote && (
                                <Box sx={{
                                  mt: 1,
                                  p: { xs: 1, sm: 1.5, md: 2 },
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 200, 0.08)' : 'rgba(76, 175, 200, 0.06)',
                                  borderLeft: `4px solid ${theme.palette.mode === 'dark' ? '#4cbfc8' : '#0097a7'}`,
                                  borderRadius: 1.5,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                  transition: 'all 0.2s ease',
                                  overflow: 'hidden',
                                  '&:hover': {
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 200, 0.12)' : 'rgba(76, 175, 200, 0.09)',
                                  }
                                }}>
                                  <FormatQuoteIcon sx={{ fontSize: { xs: 16, md: 22 }, color: theme.palette.mode === 'dark' ? '#4cbfc8' : '#0097a7', opacity: 0.85, flexShrink: 0, mt: 0.3 }} />
                                  <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 600, color: theme.palette.mode === 'dark' ? '#b0dfe6' : '#00695c', lineHeight: 1.6, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, letterSpacing: { xs: 0.1, md: 0.3 }, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>{m.quote}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                {/* VIP Guests */}
                {vips.filter(v => v.subtype === 'normal' || v.subtype === 'vip').length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mb: { xs: 1.5, md: 2 }, mt: 1, fontWeight: 700, color: 'primary.main', letterSpacing: { xs: 0.3, md: 0.5 }, fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.3rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>VIP Guests</Typography>
                    <Grid container spacing={{ xs: 1.2, sm: 1.5, md: 2 }} sx={{ mb: 2 }}>
                      {vips.filter(v => v.subtype === 'normal' || v.subtype === 'vip').map((n, i, arr) => (
                        <Grid item xs={12} sm={6} md={arr.length > 3 ? 4 : 6} lg={arr.length > 6 ? 3 : 4} key={i}>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            height: '100%',
                            borderRadius: { xs: 1.5, md: 2 },
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(35,35,58,0.92)' : 'rgba(250,250,250,0.92)',
                            p: { xs: 1, sm: 1.5, md: 2 },
                            minHeight: { xs: 180, md: 220 },
                            boxShadow: 0,
                            border: theme.palette.mode === 'dark' ? '1.5px solid #23233a' : '1.5px solid #eee',
                            transition: 'all 0.2s ease',
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: 4,
                              transform: 'translateY(-2px)',
                            },
                          }}>
                            {n.image && (
                              <Box sx={{ width: { xs: 56, sm: 64, md: 80 }, height: { xs: 56, sm: 64, md: 80 }, overflow: 'hidden', borderRadius: '50%', mb: 1, boxShadow: 2, flexShrink: 0 }}>
                                <img src={n.image} alt={n.title || n.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </Box>
                            )}
                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'primary.main', mb: 0.5, textAlign: 'center', letterSpacing: { xs: 0.3, md: 0.5 }, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem' }, whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'visible', lineHeight: 1.3 }}>{n.title || n.name}</Typography>
                            {n.description && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, textAlign: 'center', fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' }, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4, overflow: 'hidden' }}>{n.description}</Typography>}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                {/* Speakers */}
                {vips.filter(v => v.subtype === 'speaker').length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mb: { xs: 1.5, md: 2 }, mt: 1, fontWeight: 700, color: 'primary.main', letterSpacing: { xs: 0.3, md: 0.5 }, fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.3rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>Speakers</Typography>
                    <Grid container spacing={{ xs: 1.2, sm: 1.5, md: 2 }} sx={{ mb: 2 }}>
                      {vips.filter(v => v.subtype === 'speaker').slice(0, 8).map((s, i) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={i} sx={{ minHeight: { xs: 170, md: 240 } }}>
                          <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 1.5, md: 2 }, boxShadow: 2, borderRadius: { xs: 1.5, md: 2 }, bgcolor: theme.palette.mode === 'dark' ? '#23233a' : '#fff', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', minHeight: { xs: 170, md: 240 }, overflow: 'hidden' }}>
                            <Box sx={{ overflow: 'hidden' }}>
                              <img src={s.image} alt={s.name} style={{ width: 'clamp(50px, 14vw, 80px)', height: 'clamp(50px, 14vw, 80px)', objectFit: 'cover', borderRadius: '50%', marginBottom: 8, boxShadow: '0 2px 8px #0002', margin: '0 auto 8px' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem' }, whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'visible', lineHeight: 1.3 }}>{s.title || s.name}</Typography>
                            </Box>
                            {s.description && <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' }, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, overflow: 'hidden' }}>{s.description}</Typography>}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                <Button variant="contained" href="/vips" sx={{ mt: { xs: 1, sm: 1.5, md: 2 }, fontWeight: 700, borderRadius: { xs: 1.5, md: 2 }, px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 0.6, sm: 0.7, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>View All VIPs & Speakers</Button>
              </>
            )}
          </Box>
        </Box>
        {/* Expo Details Section */}
        <Box sx={{ mt: { xs: 4, sm: 5, md: 8 }, mb: { xs: 4, sm: 5, md: 8 }, px: { xs: 1.5, sm: 2, md: 4 }, py: { xs: 2, sm: 3, md: 6 }, maxWidth: 1200, mx: 'auto', width: '100%', overflow: 'visible', boxSizing: 'border-box' }}>
          {/* Main Heading */}
          <Box sx={{ mb: { xs: 2.5, sm: 3.5, md: 6 }, textAlign: 'center', width: '100%', overflow: 'hidden' }}>
            <Typography variant="h2" fontWeight="900" color="primary" sx={{ mb: { xs: 0.5, sm: 1, md: 1 }, letterSpacing: { xs: 0.5, md: 1.5 }, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3.2rem' }, textShadow: theme => theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.1)', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word', whiteSpace: 'normal' }}>
              Timte 2026 - Expo Innovation Hub
            </Typography>
            <Box sx={{ width: { xs: 60, md: 80 }, height: { xs: 3, md: 4 }, bgcolor: 'secondary.main', mx: 'auto', borderRadius: 2, boxShadow: 3, mb: { xs: 1, md: 2 } }} />
            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: { xs: 1, md: 1.5 }, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.2rem' } }}>
              Tanzania International Medical Tourism Expo
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.1rem' }, lineHeight: 1.5, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
              Transform Healthcare, Build Connections, Drive Change
            </Typography>
          </Box>

          {/* Expo Theme */}
          <Box sx={{ mb: { xs: 2.5, sm: 3, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1, md: 1.5 }, mb: { xs: 1, sm: 1.5, md: 2.5 }, overflow: 'hidden', flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: 32, sm: 36, md: 44 }, height: { xs: 32, sm: 36, md: 44 }, borderRadius: 2, bgcolor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2, flexShrink: 0 }}>
                <EmojiEventsIcon sx={{ fontSize: { xs: 24, sm: 26, md: 28 }, color: '#fff' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.6rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>
                Expo Theme
              </Typography>
            </Box>
            <Box sx={{ bgcolor: 'background.paper', borderRadius: { xs: 2, md: 3 }, p: { xs: 1.5, sm: 2, md: 3.5 }, boxShadow: 3, border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(76,175,200,0.15)' : '1px solid rgba(0,97,167,0.1)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', overflow: 'hidden', width: '100%' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: { xs: 1, md: 1.5 }, color: theme => theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main, fontSize: { xs: '0.95rem', sm: '1rem', md: '1.25rem' }, fontStyle: 'italic', lineHeight: 1.5, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                "Transforming Healthcare Through Medical Tourism: Innovation, Collaboration, and Access"
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }, lineHeight: 1.7, textAlign: 'center', color: 'text.secondary', fontWeight: 500, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                This theme aligns directly with President Samia Suluhu Hassan's healthcare vision, emphasizing improved quality of care, greater accessibility for citizens, and enhanced global competitiveness of Tanzania's health sector.
              </Typography>
            </Box>
          </Box>

          {/* Objectives */}
          <Box sx={{ mb: { xs: 2.5, sm: 3, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1, md: 1.5 }, mb: { xs: 1, sm: 1.5, md: 2.5 }, overflow: 'hidden', flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: 32, sm: 36, md: 44 }, height: { xs: 32, sm: 36, md: 44 }, borderRadius: 2, bgcolor: '#ff7043', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2, flexShrink: 0 }}>
                <GavelIcon sx={{ fontSize: { xs: 22, sm: 24, md: 28 }, color: '#fff' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.6rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>
                Objectives
              </Typography>
            </Box>
            <Box sx={{ bgcolor: 'background.paper', borderRadius: { xs: 2, md: 3 }, p: { xs: 1.5, sm: 2, md: 3.5 }, boxShadow: 3, border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(255,112,67,0.15)' : '1px solid rgba(255,112,67,0.1)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', overflow: 'hidden', width: '100%' }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }, lineHeight: 1.7, textAlign: 'justify', color: 'text.secondary', overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                The main objective of the Tanzania International Medical Tourism Expo is to <Box component="span" sx={{ fontWeight: 700, color: '#ff7043' }}>position Tanzania as a leading destination for medical tourism</Box>. This will be achieved by showcasing national healthcare capabilities, fostering international collaborations, and educating the public about available specialized healthcare services within the country.
              </Typography>
            </Box>
          </Box>

          {/* Daily Themes and Activities */}
          <Box sx={{ mb: { xs: 2.5, sm: 3, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1, md: 1.5 }, mb: { xs: 1, sm: 1.5, md: 2.5 }, overflow: 'hidden', flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: 32, sm: 36, md: 44 }, height: { xs: 32, sm: 36, md: 44 }, borderRadius: 2, bgcolor: '#29b6f6', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2, flexShrink: 0 }}>
                <CategoryIcon sx={{ fontSize: { xs: 22, sm: 24, md: 28 }, color: '#fff' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.6rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>
                Daily Themes & Activities
              </Typography>
            </Box>
            <Box sx={{ bgcolor: 'background.paper', borderRadius: { xs: 2, md: 3 }, p: { xs: 1.5, sm: 2, md: 3.5 }, boxShadow: 3, border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(41,182,246,0.15)' : '1px solid rgba(41,182,246,0.1)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', overflow: 'hidden', width: '100%' }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }, lineHeight: 1.7, mb: { xs: 1, md: 2 }, color: 'text.secondary', overflow: 'hidden', wordBreak: 'break-word' }}>
                The Tanzania Medical Tourism Expo 2025 is structured as an <Box component="span" sx={{ fontWeight: 700, color: '#29b6f6' }}>informative and impactful event</Box> featuring daily themes presented by medical experts:
              </Typography>
              <Box sx={{ pl: { xs: 0.5, sm: 1, md: 1.5 }, overflow: 'hidden' }}>
                {[
                  'Exhibitions showcasing advanced medical services and technologies',
                  'Seminars and workshops led by global healthcare experts',
                  'Parallel sessions and interactive engagements designed to educate and connect participants'
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: { xs: 0.6, sm: 0.8, md: 1.2 }, mb: { xs: 1, sm: 1.2, md: 1.5 }, alignItems: 'flex-start', overflow: 'hidden' }}>
                    <StarIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, color: '#29b6f6', flexShrink: 0, mt: 0.2 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' }, color: 'text.secondary', lineHeight: 1.6, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Key Features */}
          <Box sx={{ mb: { xs: 2.5, sm: 3, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1, md: 1.5 }, mb: { xs: 1, sm: 1.5, md: 2.5 }, overflow: 'hidden', flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: 32, sm: 36, md: 44 }, height: { xs: 32, sm: 36, md: 44 }, borderRadius: 2, bgcolor: '#ab47bc', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2, flexShrink: 0 }}>
                <StarIcon sx={{ fontSize: { xs: 22, sm: 24, md: 28 }, color: '#fff' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.6rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>
                Key Features for Attendees
              </Typography>
            </Box>
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              {[
                { icon: <MonetizationOnIcon />, title: 'Free Health Screenings', desc: 'Access complimentary health screenings for all attendees', color: '#4caf50' },
                { icon: <PeopleIcon />, title: 'Networking Sessions', desc: 'Connect with investors and healthcare professionals', color: '#00bcd4' },
                { icon: <HotelIcon />, title: 'Live Demonstrations', desc: 'Experience live medical procedures and healthcare innovations', color: '#ff9800' },
                { icon: <LocationCityIcon />, title: 'Hospital Partnerships', desc: 'International partnerships focused on technology transfer', color: '#e91e63' }
              ].map((feature, i) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={i}>
                  <Box sx={{
                    bgcolor: 'background.paper',
                    borderRadius: { xs: 2, md: 3 },
                    p: { xs: 1.2, sm: 1.5, md: 2 },
                    boxShadow: 2,
                    border: theme => theme.palette.mode === 'dark' ? `1px solid ${feature.color}22` : `1px solid ${feature.color}18`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                    '&:hover': {
                      transform: { xs: 'translateY(-3px)', md: 'translateY(-8px)' },
                      boxShadow: theme => theme.palette.mode === 'dark' ? `0 16px 40px rgba(${parseInt(feature.color.slice(1, 3), 16)},${parseInt(feature.color.slice(3, 5), 16)},${parseInt(feature.color.slice(5, 7), 16)},0.3)` : `0 16px 40px ${feature.color}30`,
                      bgcolor: theme => theme.palette.mode === 'dark' ? `${feature.color}08` : `${feature.color}06`,
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    height: '100%',
                    gap: { xs: 0.6, sm: 0.8, md: 1.2 },
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ width: { xs: 36, sm: 40, md: 48 }, height: { xs: 36, sm: 40, md: 48 }, borderRadius: 2, bgcolor: `${feature.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: `0 4px 12px ${feature.color}40`, transition: 'all 0.3s ease', fontSize: { xs: 20, sm: 24, md: 28 }, flexShrink: 0 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: feature.color, fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.05rem' }, letterSpacing: { xs: 0.2, md: 0.3 }, overflow: 'hidden', wordBreak: 'break-word' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }, color: 'text.secondary', lineHeight: 1.5, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Topics & Seminars */}
          <Box sx={{ mb: { xs: 1.5, sm: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1, md: 1.5 }, mb: { xs: 1, sm: 1.5, md: 2.5 }, overflow: 'hidden', flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: 32, sm: 36, md: 44 }, height: { xs: 32, sm: 36, md: 44 }, borderRadius: 2, bgcolor: '#ffa726', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2, flexShrink: 0 }}>
                <ConfirmationNumberIcon sx={{ fontSize: { xs: 22, sm: 24, md: 28 }, color: '#fff' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.6rem' }, overflow: 'hidden', wordBreak: 'break-word' }}>
                Key Seminar Subtopics
              </Typography>
            </Box>
            <Box sx={{ bgcolor: 'background.paper', borderRadius: { xs: 2, md: 3 }, p: { xs: 1.5, sm: 2, md: 3.5 }, boxShadow: 3, border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(255,167,38,0.15)' : '1px solid rgba(255,167,38,0.1)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', overflow: 'hidden', width: '100%' }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }, lineHeight: 1.7, mb: { xs: 1, md: 2 }, color: 'text.secondary', overflow: 'hidden', wordBreak: 'break-word' }}>
                These discussions aim to <Box component="span" sx={{ fontWeight: 700, color: '#ffa726' }}>attract investors, encourage innovation, and strengthen the healthcare ecosystem</Box>:
              </Typography>
              <Box sx={{ pl: { xs: 0.5, sm: 1, md: 1.5 }, overflow: 'hidden' }}>
                {[
                  'Investing in health infrastructure in Tanzania',
                  'Innovation and technology in the health sector'
                ].map((topic, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: { xs: 0.6, sm: 0.8, md: 1 }, mb: { xs: 1, sm: 1.2, md: 1.5 }, alignItems: 'flex-start', overflow: 'hidden' }}>
                    <CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 }, color: '#ffa726', flexShrink: 0, mt: 0.1 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' }, color: 'text.secondary', lineHeight: 1.6, fontWeight: 500, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {topic}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
        {/* 5. Venue Details Section */}
        <Box sx={{ mt: { xs: 4, sm: 5, md: 6 }, mb: { xs: 3, sm: 4, md: 4 }, px: { xs: 1.5, sm: 2, md: 2 }, maxWidth: 1200, mx: 'auto', width: '100%', overflow: 'hidden' }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: { xs: 1.5, sm: 2, md: 3 }, textAlign: 'center', fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2rem' }, color: theme => theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>Venue & Accommodation</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', px: { xs: 0, sm: 1, md: 2 }, overflow: 'hidden' }}>
            <Box sx={{ width: '100%', maxWidth: 1000, bgcolor: 'background.paper', borderRadius: { xs: 2, md: 3 }, boxShadow: 3, overflow: 'hidden', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
              <Box sx={{ flex: 1, minHeight: { xs: 160, sm: 200, md: 240 }, position: 'relative', width: { xs: '100%', md: 'auto' } }}>
                <img src="https://www.easytravel.co.tz/wp-content/uploads/2023/08/ariel-view-of-dar-es-salaam-city-easy-travel-tanzania.jpg" alt="Venue & Accommodation" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Chip label="Venue" icon={<LocationCityIcon />} size="small" sx={{ position: 'absolute', left: { xs: 8, md: 12 }, top: { xs: 8, md: 12 }, fontWeight: 800, bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.7)', color: theme => theme.palette.mode === 'dark' ? '#fff' : '#111', fontSize: { xs: '0.7rem', md: '0.8rem' } }} />
                <Chip label="Accommodation" icon={<KingBedIcon />} size="small" sx={{ position: 'absolute', left: { xs: 8, md: 12 }, top: { xs: 38, md: 56 }, fontWeight: 800, bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.65)', color: theme => theme.palette.mode === 'dark' ? '#fff' : '#111', fontSize: { xs: '0.7rem', md: '0.8rem' } }} />
              </Box>
              <Box sx={{ flex: '0 0 auto', p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: { xs: 0.8, sm: 1, md: 2 }, overflow: 'hidden' }}>
                <Typography variant="h6" fontWeight={900} sx={{ textAlign: { xs: 'center', md: 'left' }, fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.3rem' }, color: theme => theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main, overflow: 'hidden', wordBreak: 'break-word' }}>Venue & Accommodation</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 0.5, md: 1 }, textAlign: { xs: 'center', md: 'left' }, fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' }, lineHeight: 1.5, overflow: 'hidden', wordBreak: 'break-word' }}>Explore our event venues and nearby accommodation options.</Typography>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, flexDirection: { xs: 'column', sm: 'row' }, mt: { xs: 0.5, md: 1 }, justifyContent: { xs: 'center', md: 'flex-start' }, alignItems: 'center', overflow: 'hidden' }}>
                  <Button href="/venues" variant="contained" color="primary" startIcon={<LocationCityIcon />} size={isXs ? 'small' : 'medium'} sx={{ fontWeight: 800, borderRadius: { xs: 1.5, md: 3 }, px: { xs: 1.2, sm: 2, md: 3 }, py: { xs: 0.6, sm: 0.8, md: 1.1 }, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 'auto' }, background: theme => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, boxShadow: 3, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                    Explore Venue
                  </Button>
                  <Button href="/accommodation" variant="outlined" color="secondary" startIcon={<KingBedIcon />} size={isXs ? 'small' : 'medium'} sx={{ fontWeight: 800, borderRadius: { xs: 1.5, md: 3 }, px: { xs: 1.2, sm: 2, md: 3 }, py: { xs: 0.6, sm: 0.8, md: 1.1 }, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 'auto' }, borderWidth: 2, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>
                    Accommodation
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* 6. Event Highlights Section (Horizontal Carousel, Enhanced UI, Culture Theme) */}
        <Box sx={{
          mt: { xs: 3, sm: 4, md: 6 },
          mb: { xs: 2, sm: 3, md: 4 },
          position: 'relative',
          overflow: 'visible',
          minHeight: { xs: 450, md: 520 },
          px: { xs: 1.5, sm: 2, md: 3 },
          boxSizing: 'border-box',
          width: '100%'
        }}>
          {/* Culture/mountain/baobab background - more visible and adaptive */}
          <Box sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(120deg, #23233a 60%, #2e2e4d 100%)'
              : 'linear-gradient(120deg, #e0f2f1 60%, #f3f7fa 100%)',
          }} />
          {/* SVG baobab/mountain accent (bottom right, more contrast) */}
          <Box sx={{
            position: 'absolute',
            right: -20,
            bottom: -8,
            width: 180,
            height: 90,
            zIndex: 1,
            opacity: theme.palette.mode === 'dark' ? 0.22 : 0.28,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 2px 8px #0002)'
          }}>
            <svg width="180" height="90" viewBox="0 0 180 90" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="90" cy="80" rx="90" ry="12" fill="#b7e8e0" />
              <rect x="130" y="40" width="14" height="32" rx="6" fill="#8d6e63" />
              <ellipse cx="137" cy="44" rx="24" ry="12" fill="#a5d6a7" />
              <ellipse cx="146" cy="54" rx="13" ry="7" fill="#81c784" />
              <ellipse cx="122" cy="58" rx="10" ry="5" fill="#aed581" />
            </svg>
          </Box>
          {/* Decorative gadgets */}
          <Box sx={{
            position: 'absolute',
            left: -40,
            top: -30,
            width: 120,
            height: 120,
            bgcolor: theme.palette.mode === 'dark' ? '#fff1' : '#e8b7d6',
            borderRadius: '50%',
            zIndex: 0,
            filter: 'blur(8px)',
            opacity: 0.18
          }} />
          <Box sx={{
            position: 'absolute',
            right: -30,
            bottom: -30,
            width: 80,
            height: 80,
            bgcolor: theme.palette.mode === 'dark' ? '#fff2' : '#b7e8e0',
            borderRadius: '50%',
            zIndex: 0,
            filter: 'blur(8px)',
            opacity: 0.13
          }} />
          <Typography variant="h4" fontWeight="bold" color="primary" align="center" sx={{ mb: 2, letterSpacing: 1.2, textTransform: 'uppercase', zIndex: 2, position: 'relative', fontSize: { xs: '1.6rem', md: '2.2rem' }, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>Event Highlights</Typography>
          <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary', fontWeight: 500, zIndex: 2, position: 'relative', fontSize: { xs: '1rem', md: '1.1rem' }, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>Discover memorable moments and key stories from our event.</Typography>
          {highlightsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 380, md: 420 } }}>
              <CircularProgress />
            </Box>
          ) : highlightsError ? (
            <Typography color="error" align="center">{highlightsError}</Typography>
          ) : highlights.length === 0 ? (
            <Typography color="text.secondary" align="center">No highlights available at this time.</Typography>
          ) : (
            <Box onMouseEnter={() => setIsHighlightsHover(true)} onMouseLeave={() => setIsHighlightsHover(false)} sx={{ position: 'relative', width: '100%', overflow: 'hidden', mb: 2, zIndex: 2 }}>
              <IconButton onClick={() => setActiveIndex(ai => ai - 1)} sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' } }}>
                <ArrowBackIosNewIcon />
              </IconButton>
              <Box sx={{ display: 'flex', transition: transitionDisabled ? 'none' : 'transform 0.5s cubic-bezier(.4,1.4,.6,1)', transform: `translateX(-${activeIndex * (cardWidth + 24)}px)`, gap: 0, pl: 0, pr: 0 }}>
                {[...displayedHighlights, ...displayedHighlights, ...displayedHighlights, ...displayedHighlights].map((item, idx) => {
                  const L = displayedHighlights.length || 1;
                  const logicalIdx = idx % L;
                  return (
                    <Box
                      key={idx}
                      onClick={() => { setDialogIndex(logicalIdx); setReadingMode(false); setActiveIndex(idx); }}
                      sx={{
                        minWidth: { xs: 300, sm: 320 },
                        maxWidth: cardWidth,
                        flex: `0 0 ${cardWidth}px`,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.18s, transform 0.18s',
                        boxShadow: logicalIdx === activeHighlight ? 18 : 6,
                        transform: logicalIdx === activeHighlight ? 'translateY(-6px) scale(1.04)' : 'none',
                        border: logicalIdx === activeHighlight ? `2.5px solid ${theme.palette.primary.main}` : '1.5px solid #eee',
                        background: theme.palette.mode === 'dark' ? '#23233a' : '#fff',
                        borderRadius: 6,
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': { boxShadow: 20 },
                        display: 'flex',
                        flexDirection: 'column',
                        height: { xs: 360, md: 380 },
                        marginRight: 0,
                      }}
                    >
                      <Card sx={{ overflow: 'hidden', borderRadius: 3, boxShadow: 'none', bgcolor: 'inherit', transition: 'box-shadow 0.18s' }}>
                        {item.image && (
                          <Box sx={{ width: '100%', height: { xs: 180, md: 220 }, position: 'relative', overflow: 'hidden' }}>
                            <img src={item.image} alt={item.heading} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: logicalIdx === activeHighlight ? 'none' : 'grayscale(0.08) contrast(0.92)' }} />
                            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.55) 100%)' }} />
                            <Chip label={`#${logicalIdx + 1}`} size="small" sx={{ position: 'absolute', left: 8, top: 8, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', fontWeight: 700, borderRadius: 1 }} />
                          </Box>
                        )}
                        <CardContent sx={{ p: 2, bgcolor: 'background.paper', textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                          <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 0.5, letterSpacing: 0.5, color: 'primary.main', textTransform: 'capitalize', fontSize: 16, lineHeight: 1.2 }}>{item.heading}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 15, fontWeight: 500, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.summary || item.story?.split('\n')[0] || ''}</Typography>
                        </CardContent>
                      </Card>
                      {/* Decorative accent gadget on card */}
                      <Box sx={{ position: 'absolute', right: 10, bottom: 10, width: 24, height: 24, bgcolor: theme.palette.mode === 'dark' ? '#e8b7d6' : '#b7e8e0', borderRadius: 2, opacity: 0.18, zIndex: 1 }} />
                    </Box>
                  );
                })}
              </Box>
              <IconButton onClick={() => setActiveIndex(ai => ai + 1)} sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' } }}>
                <ArrowForwardIosIcon />
              </IconButton>

              {/* Progress Dots / Completion Indicator */}
              <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: -22, display: 'flex', gap: 6, alignItems: 'center', zIndex: 5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {displayedHighlights.map((_, i) => (
                    <Box key={i} onClick={() => setActiveIndex(i + displayedHighlights.length)} sx={{ width: 12, height: 12, borderRadius: '50%', cursor: 'pointer', bgcolor: i === activeHighlight ? theme.palette.primary.main : 'rgba(0,0,0,0.12)' }} />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Detail dialog (unchanged) */}
          {dialogIndex !== null && highlights[dialogIndex] && (
            <Box
              component="div"
              sx={{
                position: 'fixed',
                zIndex: 1300,
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
              }}
              onClick={() => { setDialogIndex(null); setReadingMode(false); }}
            >
              <Card
                sx={{
                  minWidth: { xs: 320, sm: 500, md: 700 },
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  borderRadius: 4,
                  boxShadow: 24,
                  position: 'relative',
                  bgcolor: theme.palette.mode === 'dark' ? '#181828' : '#fff',
                  p: 0
                }}
                onClick={e => e.stopPropagation()}
              >
                <Box sx={{ position: 'relative' }}>
                  {highlights[dialogIndex].image && (
                    <Box sx={{ width: '100%', height: { xs: 220, sm: 340 }, overflow: 'hidden', bgcolor: 'grey.900' }}>
                      <img src={highlights[dialogIndex].image} alt={highlights[dialogIndex].heading} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </Box>
                  )}
                  <IconButton onClick={() => setDialogIndex(null)} sx={{ position: 'absolute', right: 8, top: 8, color: theme.palette.mode === 'dark' ? '#fff' : '#222', bgcolor: 'rgba(0,0,0,0.10)' }}>
                    <ArrowForwardIosIcon sx={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                  {highlights.length > 1 && (
                    <>
                      <IconButton onClick={() => setDialogIndex(i => (i - 1 + highlights.length) % highlights.length)} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' } }}>
                        <ArrowBackIosNewIcon />
                      </IconButton>
                      <IconButton onClick={() => setDialogIndex(i => (i + 1) % highlights.length)} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' } }}>
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
                <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                  <Typography variant="h4" fontWeight={900} color="primary" sx={{ mb: 2, letterSpacing: 1.2, textAlign: 'center', textTransform: 'uppercase' }}>{highlights[dialogIndex].heading}</Typography>
                  {!readingMode ? (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.primary', fontSize: 18, textAlign: 'center', fontStyle: 'italic' }}>{highlights[dialogIndex].story}</Typography>
                  ) : (
                    <Box sx={{ minHeight: 240 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', textAlign: 'center' }}>Reading Mode</Typography>
                      <Typography variant="body1" sx={{ fontSize: 18, lineHeight: 1.8, whiteSpace: 'pre-line', textAlign: 'center' }}>{highlights[dialogIndex].story}</Typography>
                    </Box>
                  )}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button onClick={() => setReadingMode(m => !m)} color="primary" variant="contained">{readingMode ? 'Exit Reading' : 'Reading Mode'}</Button>
                    <Button onClick={() => { setDialogIndex(null); setReadingMode(false); }} color="inherit" variant="outlined">Close</Button>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mt: 2 }}>{dialogIndex + 1} / {highlights.length}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
        {/* 7. Sponsors Section (Dynamic, Round Images, API) */}
        <Box sx={{
          mt: { xs: 3, sm: 4, md: 6 },
          mb: { xs: 2, sm: 3, md: 4 },
          mx: { xs: 1.5, sm: 2, md: 'auto' },
          px: { xs: 1.5, sm: 1.5, md: 2 },
          py: { xs: 2.5, sm: 3, md: 5 },
          borderRadius: 5,
          position: 'relative',
          overflow: 'visible',
          width: { xs: 'calc(100% - 3px)', sm: 'calc(100% - 4px)', md: 'auto' },
          maxWidth: 1200,
          boxSizing: 'border-box',
          background: theme => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #23272f 0%, #2d3748 100%)'
            : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: 3,
          minHeight: { xs: 450, md: 520 }
        }}>
          <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center', maxWidth: 900, mx: 'auto', px: { xs: 1.5, md: 2 }, overflow: 'hidden' }}>
            <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: { xs: 2, md: 3 }, letterSpacing: { xs: 0.5, md: 1.2 }, fontSize: { xs: '1.4rem', md: '2rem' }, textShadow: theme => theme.palette.mode === 'dark' ? '0 2px 8px #0008' : '0 2px 8px #fff8', overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>Become a Sponsor</Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.8, color: 'text.secondary', fontWeight: 500, mb: 3, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>
              Organizations and partners are invited to become sponsors and actively participate in shaping the future of medical tourism in Tanzania. Sponsorship provides <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>strategic visibility</Box>, <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>influence</Box>, and <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>long-term partnership opportunities</Box> within the healthcare and investment landscape.
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: { xs: 2, md: 3 }, textAlign: 'center', letterSpacing: { xs: 0.3, md: 1 }, fontSize: { xs: '1.35rem', md: '1.6rem' }, overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'normal' }}>Expo Sponsors</Typography>
          {sponsorsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}><CircularProgress /></Box>
          ) : sponsorsError ? (
            <Typography color="error" align="center">{sponsorsError}</Typography>
          ) : sponsors.length === 0 ? (
            <Typography color="text.secondary" align="center">No sponsors available at this time.</Typography>
          ) : (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 3, sm: 4, md: 6 },
              py: 2,
              width: '100%',
              mx: 'auto',
              maxWidth: 1400,
              minHeight: 120,
            }}>
              {sponsors.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1, minWidth: 120 }}>
                  <Box sx={{
                    width: { xs: 84, sm: 96, md: 120 },
                    height: { xs: 84, sm: 96, md: 120 },
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.mode === 'dark' ? '#23272f' : '#ffffff',
                    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'}`,
                    boxShadow: theme.palette.mode === 'dark' ? '0 6px 20px rgba(0,0,0,0.6)' : '0 8px 30px rgba(2,6,23,0.06)',
                    transition: 'transform 220ms ease, box-shadow 220ms ease',
                    '&:hover': { transform: 'scale(1.06)', boxShadow: theme.palette.mode === 'dark' ? '0 10px 36px rgba(0,0,0,0.7)' : '0 12px 40px rgba(2,6,23,0.12)' },
                    mb: 1.5
                  }}>
                    <Box component="img" src={s.logo} alt={s.name} sx={{ width: '70%', height: '70%', objectFit: 'contain', transition: 'transform 320ms ease', '&:hover': { transform: 'scale(1.12)' } }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? '#fff' : 'text.secondary', textAlign: 'center', fontSize: { xs: '0.9rem', md: 16 }, letterSpacing: 0.5, textShadow: theme.palette.mode === 'dark' ? '0 1px 4px #0008' : '0 1px 4px #fff8' }}>{s.name}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {/* 8. CTA Buttons */}
        <Box sx={{ my: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center' }}>
          <CTAButtons />
        </Box>
        {/* Add Book Now button and BookingModal */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button variant="contained" color="primary" size="large" sx={{ fontWeight: 700, borderRadius: 2 }} onClick={() => setBookingOpen(true)}>
            Book Now
          </Button>
        </Box>
        <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
      </Box>
    </PublicLayout >
  );
}

export default HomePage;
